"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
const YOUTUBE_URL = "https://www.youtube.com/@yohanneswakgariofficialcha6717";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3].map((_, i) => <div key={i} className="h-4 w-16 bg-white/10 animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[14px] uppercase tracking-[0.15em] font-sans font-bold text-white/90 hover:text-[#FF6A00] transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  return <div id="mobile-menu" className={cn("fixed inset-0 z-[60] md:hidden flex flex-col bg-[#222222]/95 backdrop-blur-xl transition-all duration-300", isOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full pointer-events-none")}>
      {}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <Link to="/HomePage" onClick={onClose} className="flex flex-col items-start">
          <span className="text-xl font-extrabold tracking-wide text-white">
            ZemaYohannes
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-sans leading-none mt-1">
            Worship Ministry
          </span>
        </Link>

        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close menu" className="text-white">
          <X className="size-7" />
        </Button>
      </div>

      {}
      <div className="flex-1 overflow-y-auto py-12 px-8 flex flex-col gap-8">
        {loading ? <div className="flex flex-col gap-6">
            {[1, 2, 3].map((_, i) => <div key={i} className="h-10 w-48 bg-white/10 animate-pulse rounded" />)}
          </div> : <nav className="flex flex-col gap-8">
            {menuItems.map(item => <div key={item.id}>
                <Link to={item.href || "#"} onClick={onClose} className="text-3xl font-serif font-extrabold tracking-tight text-white hover:text-[#FF6A00] transition-colors block" {...item.href?.startsWith("http") ? {
            target: "_blank",
            rel: "noopener noreferrer"
          } : {}}>
                  {item.label}
                </Link>
              </div>)}
          </nav>}

        {}
        <div className="mt-auto pt-10 border-t border-white/10 flex flex-col gap-4">
          <Button asChild className="w-full h-14 font-bold uppercase">
            <Link to={`${YOUTUBE_URL}?sub_confirmation=1`} onClick={onClose} target="_blank">
              Subscribe
            </Link>
          </Button>

          <Button asChild className="w-full h-14 font-bold uppercase">
            <Link to={YOUTUBE_URL} onClick={onClose} target="_blank">
              Visit YouTube
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMobileMenuOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);
  return <MenuProvider menu_id="16">
      <header className={cn("sticky top-0 z-50 w-full transition-all duration-500", isScrolled ? "bg-[#222222]/90 backdrop-blur-md border-b border-white/10" : "bg-[#222222]")}>
        <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          {}
          <Link to="/HomePage" className={cn("relative text-white text-xl font-extrabold tracking-wide", "transition-all duration-300 ease-out", "hover:text-[#FF6A00] hover:tracking-wider", "after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0", "after:bg-[#FF6A00] after:transition-all after:duration-300", "hover:after:w-full", "animate-fade-slide")}>
            ZemaYohannes
          </Link>

          {}
          <div className="hidden md:flex items-center gap-4">
            <DesktopNav />
          </div>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-white" onClick={() => setIsMobileMenuOpen(true)} aria-expanded={isMobileMenuOpen} aria-controls="mobile-menu">
            <Menu className="size-6" />
          </Button>
        </div>
      </header>

      {}
      <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </MenuProvider>;
}